import React, { useEffect, useState } from 'react';
import { getOrderItemsByOrderId, getAlbaranPdf, getSignedAlbaranPdf, getOrderPhotos, updateOrder, logError } from '../api';
import PhotoCaptureModal from './PhotoCaptureModal';
import DeleteConfirmationModal from './Global/DeleteConfirmationModal';
import EditCancelModal from './Global/EditCancelModal';
import SignatureModal from './SignatureModal'; // Importa el modal de firma
import { useLocation, useNavigate } from 'react-router-dom';
import IncidentForm from './IncidentForm'; // Importa el nuevo formulario
import ItemsInfoRow from './Global/ItemsInfoRow'; // Importa el nuevo formulario

const DeliveryDispatch = () => {
  const location = useLocation();
  const { order } = location.state || {};
  const navigate = useNavigate();

  const [photos, setPhotos] = useState([]);
  const [comments, setComments] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPhotoViewOpen, setIsPhotoViewOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletePhotoIndex, setDeletePhotoIndex] = useState(null);
  const [isEditCancelModalOpen, setIsEditCancelModalOpen] = useState(false);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false); // Estado para el modal de firma
  const [pdfData, setPdfData] = useState(null); // Estado para los datos del PDF
  const [pdfName, setPdfName] = useState(null); // Estado para los datos del PDF
  const [isAlbaranSigned, setIsAlbaranSigned] = useState(false); // Estado que indica si el albarán está firmado
  const [signedPdf, setSignedPdf] = useState(null); // Estado para el PDF firmado
  const [hasIncident, setHasIncident] = useState(false); // Estado para el checkbox de incidencia
  const [isIncidentFormVisible, setIsIncidentFormVisible] = useState(false); // Controla la visibilidad del formulario de incidencias



  const formatDate = (date) => new Date(date).toLocaleDateString('es-ES');

  useEffect(() => {
    const fetchItems = async () => {
      if (!order?.id) return;

      try {
        const fetchedItems = await getOrderItemsByOrderId(order.id);
        
        // Verificar si el albarán ya está firmado
        if (order.signedTime) {
          setIsAlbaranSigned(true);
        }
        
        // Cargar fotos existentes
        try {
          const existingPhotos = await getOrderPhotos(order.id);
          if (existingPhotos && existingPhotos.base64Photos && existingPhotos.base64Photos.length > 0) {
            const photoUrls = existingPhotos.base64Photos
              .filter(photo => photo && photo.trim() !== '') // Filtrar fotos vacías
              .map(photo => {
                // Decodificar caracteres HTML si están presentes
                const decodedPhoto = photo.replace(/&amp;quot;/g, '"').replace(/&quot;/g, '"');
                // Limpiar el string base64 de posibles comillas
                const cleanPhoto = decodedPhoto.replace(/^"|"$/g, '');
                return `data:image/jpeg;base64,${cleanPhoto}`;
              });
            setPhotos(photoUrls);
          }
        } catch (error) {
          console.log('No hay fotos existentes o error al cargarlas:', error);
        }
      } catch (err) {
        logError(err);
        console.error('Error al cargar los artículos:', err);
      } 
      
    };

    fetchItems();
  }, [order]);

  // Obtener el PDF para el modal de firma
  const handleOpenSignatureModal = async () => {
    try {
      const response = await getAlbaranPdf(order.id); // Simulamos la obtención del albarán en PDF
      if (response && response.data && response.data.base64Pdf) {
        const base64String = response.data.base64Pdf;
        setPdfName("Firmado " + response.data.fileName);
        setPdfData(base64String); // Guardamos el PDF en el estado
        setIsSignatureModalOpen(true); // Abrimos el modal
      } else {
        alert('No se pudo obtener el albarán para firmar.');
      }
    } catch (error) {
      logError(error);
      console.error('Error al obtener el PDF:', error);
      alert('Hubo un error al obtener el albarán.');
    }
  };

  // Lógica para manejar la toma de fotos
  const handlePhotoTaken = async (newPhoto) => {
    if (photos.length < 5) {
      const updatedPhotos = [...photos, newPhoto];
      setPhotos(updatedPhotos);
      setIsModalOpen(false);
      
      // Guardar inmediatamente en la API
      await savePhotosToAPI(updatedPhotos);
    } else {
      alert('Máximo de 5 fotos alcanzado.');
    }
  };

  // Lógica para manejar la carga de fotos desde almacenamiento
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    let updatedPhotos = [...photos];
    
    for (const file of files) {
      if (updatedPhotos.length >= 5) {
        alert('Máximo de 5 fotos alcanzado.');
        break;
      }
      
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        const result = await new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(file);
        });
        updatedPhotos.push(result);
      } else {
        alert('Solo se permiten archivos de imagen.');
      }
    }
    
    setPhotos(updatedPhotos);
    
    // Guardar inmediatamente en la API si se añadieron fotos
    if (updatedPhotos.length > photos.length) {
      await savePhotosToAPI(updatedPhotos);
    }
    
    // Limpiar el input
    event.target.value = '';
  };

  // Lógica para manejar la eliminación de fotos
  const handleRemovePhoto = (index) => {
    setDeletePhotoIndex(index);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDeletePhoto = async () => {
    const updatedPhotos = photos.filter((_, i) => i !== deletePhotoIndex);
    setPhotos(updatedPhotos);
    setIsDeleteModalOpen(false);
    
    // Actualizar la API
    await savePhotosToAPI(updatedPhotos);
  };

  const handleCancelDeletePhoto = () => {
    setDeletePhotoIndex(null);
    setIsDeleteModalOpen(false);
  };

  // Lógica para ver una foto
  const handleViewPhoto = (photo) => {
    setSelectedPhoto(photo);
    setIsPhotoViewOpen(true);
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]); // Eliminar "data:image/png;base64," o "data:application/pdf;base64,"
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Función para guardar fotos en la API
  const savePhotosToAPI = async (photosArray) => {
    try {
      const deliveryData = {
        id: order.id,
        DeliveryImage1Base64: photosArray[0] ? photosArray[0].split(',')[1] : null,
        DeliveryImage2Base64: photosArray[1] ? photosArray[1].split(',')[1] : null,
        DeliveryImage3Base64: photosArray[2] ? photosArray[2].split(',')[1] : null,
        DeliveryImage4Base64: photosArray[3] ? photosArray[3].split(',')[1] : null,
        DeliveryImage5Base64: photosArray[4] ? photosArray[4].split(',')[1] : null,
      };
      
      await updateOrder(order.id, deliveryData);
    } catch (error) {
      logError(error);
      console.error('Error al guardar las fotos:', error);
      alert('Hubo un error al guardar la foto.');
    }
  };

  const handleSave = async () => {
    // Recolectamos los datos a enviar
    const deliveryData = {
      id: order.id,
      DeliveryComments: comments,
      HasIncident: hasIncident, 
      DeliveryImage1Base64: photos[0] ? photos[0] : null,  // Asumimos que photos ya están en base64
      DeliveryImage2Base64: photos[1] ? photos[1] : null,  // Asumimos que photos ya están en base64
      DeliveryImage3Base64: photos[2] ? photos[2] : null,  // Asumimos que photos ya están en base64
      DeliveryImage4Base64: photos[3] ? photos[3] : null,  // Asumimos que photos ya están en base64
      DeliveryImage5Base64: photos[4] ? photos[4] : null,  // Asumimos que photos ya están en base64
      SignedAlbaranPdfBase64: signedPdf ? await convertToBase64(signedPdf) : null, // Convertir el PDF a Base64
      Status: "Entregado",
      DeliveredTime: new Date().toISOString(), // Fecha y hora actuales
    };
  
    try {
      await updateOrder(order.id, deliveryData);
      // Después de actualizar el pedido, navegamos hacia atrás
      navigate(-1);
    } catch (error) {
      logError(error);
      console.error('Error al actualizar el pedido:', error);
      alert('Hubo un error al guardar la información.');
    }
  };
  

  // Lógica para cancelar y confirmar la cancelación
  const handleCancel = () => {
    if (photos.length > 0 || comments) {
      setIsEditCancelModalOpen(true);
    } else {
      handleConfirmCancel(); // Regresa a la página anterior
    }
  };

  const handleConfirmCancel = () => {
    navigate(-1);
  };

  const handleCancelCancel = () => {
    setIsEditCancelModalOpen(false);
  };

  const handlePdfGenerated = async (signedPdfBlob) => {
    setSignedPdf(signedPdfBlob); // Guardamos el PDF firmado en el estado
    setPdfName(`Albarán Firmado ${order.externalId}.pdf`);

    const deliveryData = {
      id: order.id,
      SignedAlbaranPdfBase64: signedPdfBlob ? await convertToBase64(signedPdfBlob) : null,
      SignedTime: new Date().toISOString(), // Fecha y hora actuales
    };
    
    await updateOrder(order.id, deliveryData);

    setIsAlbaranSigned(true); // Marcar como firmado
  };

  // Función para descargar el albarán firmado desde el servidor
  const handleDownloadSignedAlbaran = async () => {
    try {
      const response = await getSignedAlbaranPdf(order.id);
      if (response && response.data) {
        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Albarán Firmado ${order.externalId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        alert('No se pudo descargar el albarán firmado.');
      }
    } catch (error) {
      logError(error);
      console.error('Error al descargar el albarán firmado:', error);
      alert('Hubo un error al descargar el albarán firmado.');
    }
  };

  // Validación antes de guardar
  const isSaveDisabled = photos.length === 0 || !isAlbaranSigned || hasIncident;

  if (!order) {
    return (
      <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <div className="text-center">
          <div className="mb-3">❌</div>
          <h5>No hay información disponible para este pedido</h5>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-3 py-4" style={{ maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h1 className="h3 mb-0 text-primary" data-testid="dispatch-page-title">📦 Entrega</h1>
            <span className="h4 mb-0 text-dark fw-bold" data-testid="dispatch-order-id">{order.externalId}</span>
          </div>
        </div>
      </div>

      {/* Order Info Cards */}
      <div className="row mb-4">
        <div className="col-md-6 col-lg-4 mb-3">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body">
              <h6 className="card-title text-muted mb-2">💰 Información Financiera</h6>
              <div className="mb-2"><strong>Total:</strong> <span className="text-success">{order.totalAmount}€</span></div>
              <div className="mb-2"><strong>Pendiente:</strong> <span className="text-warning">{order.pendingAmount}€</span></div>
              <div><strong>Método de pago:</strong> {order.paymentMethod?.name || 'No especificado'}</div>
            </div>
          </div>
        </div>
        
        <div className="col-md-6 col-lg-4 mb-3">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body">
              <h6 className="card-title text-muted mb-2">👤 Cliente</h6>
              <div className="mb-1"><strong>{order.client?.name} {order.client?.lastName1} {order.client?.lastName2}</strong></div>
              <div className="small text-muted mb-1">📞 {order.client?.phone || 'No disponible'}</div>
              {order.client?.phone2 && <div className="small text-muted">📞 {order.client.phone2}</div>}
            </div>
          </div>
        </div>
        
        <div className="col-md-12 col-lg-4 mb-3">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body">
              <h6 className="card-title text-muted mb-2">📍 Dirección</h6>
              <div className="small">{order.address?.address}</div>
              <div className="small text-muted">{order.address?.city}, {order.address?.zipCode}, {order.address?.state}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Dates */}
      <div className="row mb-4">
        <div className="col-md-6 mb-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body py-3">
              <div className="d-flex align-items-center">
                <span className="me-2">📅</span>
                <div>
                  <div className="small text-muted">Fecha del pedido</div>
                  <div><strong>{formatDate(order.orderDate)}</strong></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6 mb-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body py-3">
              <div className="d-flex align-items-center">
                <span className="me-2">🚚</span>
                <div>
                  <div className="small text-muted">Fecha de entrega</div>
                  <div><strong>{order.deliveryDate ? formatDate(order.deliveryDate) : 'No definida'}</strong></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comments */}
      {(order.internalComments || order.externalComments) && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h6 className="card-title text-muted mb-3">💬 Comentarios</h6>
                {order.internalComments && (
                  <div className="mb-2">
                    <strong className="small text-muted">Internos:</strong>
                    <div className="small">{order.internalComments}</div>
                  </div>
                )}
                {order.externalComments && (
                  <div>
                    <strong className="small text-muted">Externos:</strong>
                    <div className="small">{order.externalComments}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Items */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="card-title text-muted mb-3">📋 Artículos</h6>
              <ItemsInfoRow order={order} />
            </div>
          </div>
        </div>
      </div>

      {/* Photos Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="card-title text-muted mb-0">📸 Fotos de Entrega</h6>
                <span className="badge badge-secondary" data-testid="photos-counter">{photos.length}/5</span>
              </div>
              
              {photos.length > 0 ? (
                <div className="row">
                  {photos.map((photo, index) => (
                    <div key={index} className="col-6 col-md-4 col-lg-3 mb-3">
                      <div className="card border-0 shadow-sm">
                        <img src={photo} alt={`Foto ${index + 1}`} className="card-img-top" style={{ height: '120px', objectFit: 'cover' }} />
                        <div className="card-body p-2">
                          <div className="d-flex gap-1">
                            <button className="btn btn-sm btn-outline-primary flex-fill" onClick={() => handleViewPhoto(photo)}>
                              Ver
                            </button>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleRemovePhoto(index)}>
                              🗑️
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  <div className="mb-2">📷</div>
                  <div>No hay fotos tomadas</div>
                </div>
              )}
              
              {photos.length < 5 && (
                <div className="text-center mt-3">
                  <div className="d-flex flex-column flex-md-row gap-2 justify-content-center">
                    <button onClick={() => setIsModalOpen(true)} className="btn btn-primary" data-testid="take-photo-btn">
                      📸 Tomar Foto
                    </button>
                    <label className="btn btn-outline-primary" style={{ cursor: 'pointer' }}>
                      📁 Cargar Imagen
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Signature Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="card-title text-muted mb-3">✍️ Firma del Albarán</h6>
              <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center">
                {isAlbaranSigned ? (
                  <>
                    <span className="badge bg-success fs-6">✅ Albarán Firmado</span>
                    <button
                      onClick={handleDownloadSignedAlbaran}
                      className="btn btn-success"
                      data-testid="download-signed-albaran-btn"
                    >
                      📥 Descargar
                    </button>
                  </>
                ) : (
                  <button onClick={handleOpenSignatureModal} className="btn btn-outline-primary" data-testid="sign-albaran-btn">
                    ✍️ Firmar Albarán
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comments and Incident */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="card-title text-muted mb-3">📝 Comentarios Adicionales</h6>
              <textarea
                className="form-control mb-3"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows="3"
                placeholder="Añadir comentarios sobre la entrega..."
                data-testid="delivery-comments-textarea"
              />
              
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="incidentCheck"
                  checked={hasIncident}
                  onChange={(e) => {
                    setHasIncident(e.target.checked);
                    setIsIncidentFormVisible(e.target.checked);
                  }}
                  data-testid="incident-checkbox"
                />
                <label className="form-check-label" htmlFor="incidentCheck">
                  ⚠️ Marcar como incidencia
                </label>
              </div>
              
              {isIncidentFormVisible && (
                <div className="mt-3">
                  <IncidentForm orderId={order.id} initialMileage={order.mileage} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="row">
        <div className="col-12">
          <div className="d-flex flex-column flex-md-row gap-3 justify-content-end">
            <button onClick={handleCancel} className="btn btn-outline-secondary" data-testid="cancel-delivery-btn">
              ❌ Cancelar
            </button>
            <button 
              onClick={handleSave} 
              className="btn btn-success" 
              disabled={isSaveDisabled}
              data-testid="complete-delivery-btn"
            >
              ✅ Completar Entrega
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      <PhotoCaptureModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onPhotoTaken={handlePhotoTaken}
      />

      {isSignatureModalOpen && (
        <SignatureModal
          pdfData={pdfData}
          orderId={order.id}
          onClose={() => setIsSignatureModalOpen(false)}
          onPdfGenerated={handlePdfGenerated}
        />
      )}

      {isPhotoViewOpen && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Vista de Foto</h5>
                <button type="button" className="btn-close" onClick={() => setIsPhotoViewOpen(false)}></button>
              </div>
              <div className="modal-body text-center">
                <img src={selectedPhoto} alt="Vista Ampliada" className="img-fluid" />
              </div>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        show={isDeleteModalOpen}
        onHide={handleCancelDeletePhoto}
        onConfirm={handleConfirmDeletePhoto}
        message="¿Estás seguro de que deseas eliminar esta foto?"
      />

      <EditCancelModal
        show={isEditCancelModalOpen}
        onConfirm={handleConfirmCancel}
        onCancel={handleCancelCancel}
      />
    </div>
  );
};

export default DeliveryDispatch;
